import { z } from 'zod';

/**
 * M5 — Cuestionarios. El corazón del producto.
 *
 * ── El requisito duro ──
 * Un cuestionario respondido **no se edita**: editarlo crea una versión nueva, y
 * cada respuesta apunta a la versión que su autor vio. Sin eso, corregir una tilde
 * invalida en silencio el «antes vs. después», que es lo que Copathy vende — los
 * números siguen saliendo, solo que comparan cosas distintas.
 *
 * Lo hace cumplir el **motor** (`tg_*_solo_borrador`, migración 011). Lo de acá es
 * el contrato; no es lo que protege.
 */

/**
 * Los diez tipos. Nueve se responden; la sección organiza.
 *
 * Los cuatro primeros llegaron con la 011; los cinco de texto y dato suelto con
 * la 015, cuando se confirmó la duda #11 — y la confirmó también el antecedente:
 * **46 de 274 respuestas en AppSheet fueron texto libre**, el segundo tipo más
 * usado (`docs/10-appsheet-antecedente.md`).
 *
 * ⚠️ **No hay tipo «0 a 10»**: eso es una `likert` con la escala «Del 0 al 10»,
 * que la 015 sembró con once puntos. Un tipo aparte sería otra rama en cada
 * `switch` para dibujar exactamente lo mismo.
 *
 * ⚠️ **No hay foto ni video**, y no es un olvido: no son un tipo de pregunta más,
 * son almacenamiento de archivos por respuesta. Y una foto que sube un trabajador
 * es un dato personal que no se puede anonimizar ni agregar. Necesita decisión
 * escrita antes que una columna.
 */
export const TIPOS_PREGUNTA = [
  'seccion',
  'likert',
  'matriz_likert',
  'barra',
  'opcion_unica',
  'opcion_multiple',
  'texto_corto',
  'texto_largo',
  'numero',
  'si_no',
  'fecha',
  /*
    Multimedia (CC-09, CC-10, migración 034).

    ⚠️ Lo que se responde con estos dos **no entra en el informe de la empresa**,
    ni con configuración ni con mínimo por grupo: una nota de voz es la voz de la
    persona y no se anonimiza. Ver la cabecera de la 034 antes de tocarlos.
  */
  'archivo',
  'nota_voz',
  'video',
] as const;
export type TipoPregunta = (typeof TIPOS_PREGUNTA)[number];

/** Cómo se agrupan en el selector. Diez botones sueltos no se eligen, se sufren. */
export const FAMILIAS_PREGUNTA = [
  { clave: 'escala', titulo: 'Medir' },
  { clave: 'eleccion', titulo: 'Elegir' },
  { clave: 'abierta', titulo: 'Escribir' },
  /*
    Familia propia para lo multimedia (034), y no dentro de «Escribir». Es lo que
    hace que en el selector de tipos aparezcan juntos y separados del resto: lo
    que se responde con ellos **no llega al informe de la empresa**, así que el
    que arma el cuestionario tiene que ver que son otra cosa antes de elegirlos.
  */
  { clave: 'multimedia', titulo: 'Adjuntar o grabar' },
  { clave: 'estructura', titulo: 'Organizar' },
] as const;
export type FamiliaPregunta = (typeof FAMILIAS_PREGUNTA)[number]['clave'];

/**
 * La paleta de acentos de una sección. **Cerrada a propósito**: un selector de
 * color libre produce formularios con doce colores peleando entre sí y contra la
 * marca, y un hex libre viaja hasta el CSS de la app móvil sin que nadie lo
 * valide. El nombre se elige acá; **el tono exacto lo decide cada pantalla** —la
 * web con sus variables, la app móvil con las suyas.
 */
export const COLORES_SECCION = [
  'marino',
  'naranja',
  'verde',
  'morado',
  'turquesa',
  'rosa',
] as const;
export type ColorSeccion = (typeof COLORES_SECCION)[number];

/** `#rrggbb`, en minúscula o mayúscula. La forma corta `#abc` NO se admite. */
export const HEX_SECCION = /^#[0-9a-fA-F]{6}$/;

/**
 * El color de una sección: **un nombre de la paleta o un hex libre** (036).
 *
 * ── Por qué dejó de ser una paleta cerrada ──
 * La 016 la cerró, y su razón sigue en pie: un selector libre produce
 * formularios con doce colores que pelean entre sí y contra el logo. **El
 * cliente lo pidió igual**, y es su decisión (D-012). Los seis nombres se quedan
 * como atajos: la paleta no desaparece, deja de ser el límite.
 *
 * ⚠️ Lo que no se deja suelto es el **contraste**: `acentoDeColor` calcula el
 * color del texto por luminancia, así que un acento claro recibe texto oscuro. Un
 * hex libre sin eso deja cabeceras ilegibles, y la regla de marca es explícita
 * (D-025).
 */
export const esquemaColorSeccion = z
  .string()
  .trim()
  .refine(
    (v) => (COLORES_SECCION as readonly string[]).includes(v) || HEX_SECCION.test(v),
    'Elige un color de la paleta o escribe un hex como #1C4270.',
  );

export const TIPO_PREGUNTA: Record<
  TipoPregunta,
  { titulo: string; ayuda: string; familia: FamiliaPregunta }
> = {
  likert: {
    titulo: 'Escala',
    ayuda: 'Una afirmación y una escala. Es el tipo principal.',
    familia: 'escala',
  },
  matriz_likert: {
    titulo: 'Matriz de escala',
    ayuda:
      'Varias afirmaciones con la misma escala, en una sola pantalla. Sin esto, quince afirmaciones son quince pantallas y la gente abandona.',
    familia: 'escala',
  },
  barra: {
    titulo: 'Barra deslizante',
    ayuda: 'Se arrastra con el dedo de un extremo al otro. Para grados, no para opciones.',
    familia: 'escala',
  },
  opcion_unica: {
    titulo: 'Selección única',
    ayuda: 'Una sola respuesta entre varias.',
    familia: 'eleccion',
  },
  opcion_multiple: {
    titulo: 'Selección múltiple',
    ayuda: 'Varias respuestas, con un mínimo y un máximo.',
    familia: 'eleccion',
  },
  si_no: {
    titulo: 'Sí o no',
    ayuda: 'Dos botones. Es una selección única, pero sin teclear las opciones cada vez.',
    familia: 'eleccion',
  },
  texto_corto: {
    titulo: 'Texto corto',
    ayuda: 'Una línea: un nombre, una palabra, una idea suelta.',
    familia: 'abierta',
  },
  texto_largo: {
    titulo: 'Texto libre',
    ayuda: 'Varios renglones. Es donde aparece lo que ninguna opción anticipó.',
    familia: 'abierta',
  },
  numero: {
    titulo: 'Número',
    ayuda: 'Una cifra, con mínimo y máximo opcionales.',
    familia: 'abierta',
  },
  fecha: { titulo: 'Fecha', ayuda: 'Un día del calendario.', familia: 'abierta' },
  /*
    ── Los textos de esta familia son cortos a propósito ──
    Explicaban en tres líneas qué pasa y qué no con el dato. Sobraba: quien arma
    los cuestionarios en Copathy sabe cuándo pedir una grabación. La garantía no
    depende de que nadie la lea —la sostienen el permiso prohibido, la ausencia
    de rama en el agregado y las políticas de RLS (034, D-138)—, así que el
    recordatorio va donde importa: en el aviso que ve el PARTICIPANTE antes de
    grabar.
  */
  archivo: {
    titulo: 'Foto o archivo',
    ayuda: 'Adjunta una imagen o un PDF. Solo lo ve COPATHY.',
    familia: 'multimedia',
  },
  nota_voz: {
    titulo: 'Nota de voz',
    ayuda: 'Graba audio, hasta 10 minutos. Solo lo oye COPATHY.',
    familia: 'multimedia',
  },
  video: {
    titulo: 'Video',
    ayuda: 'Graba o adjunta un video corto. Solo lo ve COPATHY.',
    familia: 'multimedia',
  },
  seccion: {
    titulo: 'Sección',
    ayuda:
      'Un título con subtítulo que abre una página nueva, con su propio color. Es lo que convierte treinta preguntas en un recorrido por partes.',
    familia: 'estructura',
  },
};

/** Qué tipos usan una escala Likert, y cuáles usan ítems. Una sola definición. */
export const usaEscala = (tipo: TipoPregunta) => tipo === 'likert' || tipo === 'matriz_likert';
export const usaItems = (tipo: TipoPregunta) =>
  tipo === 'opcion_unica' || tipo === 'opcion_multiple' || tipo === 'matriz_likert';

/**
 * `minimo` y `maximo` significan **cosas distintas según el tipo**, y por eso hay
 * una función que lo dice en vez de un `if` repartido por la aplicación:
 * en `opcion_multiple` son cuántas opciones se pueden marcar; en `numero`, el
 * rango del valor. En los demás no se usan.
 */
export const usaRango = (tipo: TipoPregunta) =>
  tipo === 'opcion_multiple' || tipo === 'numero' || tipo === 'barra';

/**
 * Los rótulos de los extremos: solo la barra los tiene.
 *
 * Sin ellos, una barra de 0 a 100 no dice qué significa estar a la izquierda. Son
 * opcionales —a veces el enunciado ya lo dice— pero la mayoría de las veces hacen
 * falta, y el motor los rechaza en cualquier otro tipo (migración 029).
 */
export const usaExtremos = (tipo: TipoPregunta) => tipo === 'barra';

/** El recorrido por omisión de una barra nueva. */
export const BARRA_MINIMO = 0;
export const BARRA_MAXIMO = 100;

/** Una sección no se responde: organiza. Todo lo que cuenta preguntas la salta. */
export const esSeccion = (tipo: TipoPregunta) => tipo === 'seccion';

/**
 * Los tipos que se responden **subiendo un archivo** (034).
 *
 * ⚠️ Se agrupan en un helper y no se comparan a mano en cada sitio porque de
 * esta lista dependen tres cosas que tienen que moverse juntas: qué acepta el
 * envío, qué NO va al informe de la empresa, y qué se sirve solo con
 * `resultados.ver_adjuntos`. Con comparaciones sueltas, agregar un tercer tipo
 * multimedia dejaría uno de los tres sitios atrás.
 */
export const usaArchivo = (tipo: TipoPregunta) =>
  tipo === 'archivo' || tipo === 'nota_voz' || tipo === 'video';

/** Los que miden duración: audio y video. */
export const usaDuracionMedia = (tipo: TipoPregunta) =>
  tipo === 'nota_voz' || tipo === 'video';

/**
 * Qué tipos pueden ofrecer una **observación abierta** debajo (CC-11, 035).
 *
 * ── Por qué estos cuatro y no todos ──
 * · Selección → **sí**: las opciones no siempre agotan las respuestas.
 * · `likert` y `barra` → **sí**: un «¿por qué?» detrás de un 2 vale oro.
 * · `matriz_likert` → **no**, y no por pereza: una matriz tiene N afirmaciones.
 *   Una observación para toda la matriz no dice a cuál se refiere, y una por
 *   afirmación multiplica el trabajo de responder.
 * · Los de texto → **no**: ya son un campo de escribir.
 * · `si_no`, `numero`, `fecha`, multimedia, `seccion` → **no**. Si hace falta,
 *   va una pregunta aparte.
 *
 * ⚠️ La misma lista está en `ck_preguntas_observacion_donde_va` (035). Si acá se
 * amplía y allá no, el guardado falla con un error de restricción en vez de con
 * una frase — y al revés, la pantalla ofrecería algo que el motor rechaza.
 */
export const admiteObservacion = (tipo: TipoPregunta) =>
  tipo === 'opcion_unica' || tipo === 'opcion_multiple' || tipo === 'likert' || tipo === 'barra';

/** Lo que dice el campo si no le pusieron rótulo propio. */
export const OBSERVACION_ROTULO_POR_OMISION = 'Si quieres agregar algo, escríbelo acá';



/**
 * Qué se admite en un adjunto, y por qué esta lista es corta.
 *
 * **Imágenes:** se reprocesan con `sharp`, que reescribe el archivo y **descarta
 * los metadatos** —incluido el GPS de una foto de teléfono—. Es la única familia
 * donde podemos limpiar lo que sube alguien.
 *
 * **PDF:** se guarda **tal cual**. ⚠️ Un PDF conserva sus metadatos —autor,
 * software, a veces rutas del equipo— y no hay un `sharp` para PDF. Se acepta
 * porque «adjuntar un archivo» sin PDF no sirve de mucho, pero **se sirve como
 * descarga y nunca en línea** para que no se ejecute nada en el navegador de
 * quien lo abre.
 *
 * **Lo que NO se admite:** ofimática y comprimidos. Un `.docx` con macros o un
 * `.zip` son ejecutables disfrazados, y acá el que los abriría es personal de
 * Copathy revisando respuestas — el peor sitio para un archivo que no se pudo
 * inspeccionar.
 */
export const ADJUNTO_TIPOS = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
] as const;

/**
 * Tipos de VIDEO. Lo que graban de fábrica iOS y Android.
 *
 * ⚠️ **Un video pesa otra cosa.** El tope de un adjunto son 10 MB, que para una
 * foto sobra y para video son unos 20 segundos a calidad de teléfono. Por eso
 * `VIDEO_MAX_BYTES` es aparte y más alto — y sigue siendo un tope pequeño a
 * propósito: esto es una respuesta a una pregunta, no una película.
 *
 * ⚠️ Y como el audio: **no se reprocesa**. No hay un `sharp` para video, así que
 * de lo que sube el participante no se puede quitar nada ni comprobar que sea
 * video más allá de lo que declara. Lo que lo acota es el tamaño y que la lista
 * sea corta.
 */
export const VIDEO_TIPOS = ['video/mp4', 'video/quicktime', 'video/webm'] as const;
export const VIDEO_MAX_BYTES = 50 * 1024 * 1024;
/** Un minuto. Más que eso no se revisa: se acumula. */
export const VIDEO_MAX_SEGUNDOS = 60;

/** Tipos de audio de una nota de voz. Lo que graban de fábrica iOS y Android. */
export const NOTA_VOZ_TIPOS = [
  'audio/m4a',
  'audio/mp4',
  'audio/aac',
  'audio/mpeg',
  'audio/webm',
  'audio/ogg',
] as const;

export const ADJUNTO_MAX_BYTES = 10 * 1024 * 1024;
/** Diez minutos. Más que eso no es una respuesta, es una entrevista. */
export const NOTA_VOZ_MAX_SEGUNDOS = 600;

export const ESTADOS_VERSION = ['borrador', 'publicada', 'retirada'] as const;
export type EstadoVersion = (typeof ESTADOS_VERSION)[number];

// ---------------------------------------------------------------------------
// Escalas
// ---------------------------------------------------------------------------

export const esquemaPuntoEscala = z.object({
  id: z.number().int().positive(),
  orden: z.number().int().positive(),
  etiqueta: z.string(),
  /** La carita. En AppSheet la Likert de Copathy SE RESPONDÍA con caritas. */
  emoji: z.string().nullable(),
  /**
   * La imagen del set que le toca a este punto, si la escala usa un set
   * (migración 033). **Ya resuelta acá**: el servidor empareja el orden del punto
   * con el orden de la imagen, así que quien dibuja no tiene que saber nada de
   * sets. `null` = este punto va con su emoji, o con su número.
   */
  imagenId: z.number().int().positive().nullable(),
});

export const esquemaEscala = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  activo: z.boolean(),
  puntos: z.array(esquemaPuntoEscala),
  /** El set que usa, o `null` si va con emojis. */
  setImagenesId: z.number().int().nullable(),
  /** Cuántas preguntas de versiones ya publicadas la usan. Si hay alguna, no se edita. */
  usosPublicados: z.number().int(),
});
export type Escala = z.infer<typeof esquemaEscala>;

/**
 * Un set de imágenes para escalas (CC-04, migración 033).
 *
 * Se carga una vez —cinco caritas dibujadas a la medida— y lo usan muchas
 * escalas. **Solo los ids y el orden, nunca los binarios**: cada imagen se pide
 * por su ruta autenticada, igual que la portada de un cuestionario.
 */
export const esquemaImagenDeSet = z.object({
  id: z.number().int().positive(),
  orden: z.number().int().positive(),
});

export const esquemaSetImagenes = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  activo: z.boolean(),
  imagenes: z.array(esquemaImagenDeSet),
  /**
   * Cuántas preguntas ya publicadas lo usan. Si es mayor que cero, **sus
   * imágenes no se pueden cambiar**: lo impide `tg_set_imagenes_inmutable`,
   * porque cambiar la imagen 3 cambiaría lo que la gente vio al responder.
   */
  usosPublicados: z.number().int(),
});
export type SetImagenes = z.infer<typeof esquemaSetImagenes>;

export const esquemaCrearSetImagenes = z.object({
  nombre: z.string().trim().min(3, 'Ponle un nombre reconocible al set.').max(120),
});
export type EntradaCrearSetImagenes = z.infer<typeof esquemaCrearSetImagenes>;

/** Límites de una imagen de escala. Más chicas que una portada: son iconos. */
export const IMAGEN_SET_MAX_BYTES = 2 * 1024 * 1024;
/**
 * Lado máximo tras procesar. Se dibuja a unos 40 px; 256 cubre pantallas de alta
 * densidad y sobra. **No es lo mismo que una portada** (1600): de una escala se
 * cargan hasta once imágenes de golpe, y once portadas serían megas por pregunta.
 */
export const IMAGEN_SET_LADO_MAX = 256;

export const esquemaCrearEscala = z.object({
  nombre: z.string().trim().min(3, 'Ponle un nombre reconocible.').max(120),
  /**
   * Qué set de imágenes usa. `null` = los emojis de cada punto, que sigue siendo
   * una opción válida y no un estado a medias.
   *
   * ⚠️ **El set tiene que tener exactamente tantas imágenes como puntos la
   * escala.** Las imágenes se emparejan por orden, así que un set de 3 en una
   * escala de 5 dejaría dos puntos sin figura. No cabe en un esquema de Zod
   * —hay que contar filas de otra tabla— así que lo comprueba el servicio.
   */
  setImagenesId: z.number().int().positive().nullable().optional(),
  /**
   * Las etiquetas, en orden. **Mínimo dos**: una escala de un punto no mide nada.
   * El tope de 11 cubre hasta la de 0–10.
   */
  puntos: z
    .array(
      z.object({
        etiqueta: z.string().trim().min(1, 'Cada punto necesita su etiqueta.').max(120),
        /*
          Hasta 16 caracteres: un emoji compuesto (tono de piel, bandera) llega a
          8–10. No se valida que SEA un emoji — distinguirlo de texto es una
          guerra perdida contra Unicode; lo que importa es que no quepa un párrafo.
        */
        emoji: z.string().trim().min(1).max(16).nullable().optional(),
      }),
    )
    .min(2, 'Una escala necesita al menos dos puntos.')
    .max(11, 'Más de once puntos deja de leerse como una escala.'),
});
export type EntradaCrearEscala = z.infer<typeof esquemaCrearEscala>;

/**
 * Editar una escala: **el estado final completo**, nombre y puntos.
 *
 * No es una lista de operaciones («agrega un punto», «renombra el tercero»)
 * porque una escala es una unidad: sus cinco puntos significan algo *entre
 * ellos*. Con operaciones sueltas, dos personas editando a la vez producen una
 * escala que ninguna pidió; con el estado final, la última gana — que es un
 * resultado que alguien eligió.
 *
 * ⚠️ **Los puntos se reemplazan enteros, así que solo se puede editar una escala
 * que NINGUNA versión publicada use.** Lo impide el motor —
 * `tg_escala_puntos_inmutable`, migración 011— y con razón: cambiarle el
 * significado a un punto cambiaría lo que ya se respondió. El servicio lo
 * comprueba antes para poder decirlo con palabras en vez de dejar salir el error
 * del trigger.
 */
export const esquemaEditarEscala = esquemaCrearEscala;
export type EntradaEditarEscala = z.infer<typeof esquemaEditarEscala>;

// ---------------------------------------------------------------------------
// Cuestionarios
// ---------------------------------------------------------------------------

export const esquemaItemPregunta = z.object({
  id: z.number().int().positive(),
  orden: z.number().int().positive(),
  texto: z.string(),
});

export const esquemaPregunta = z.object({
  id: z.number().int().positive(),
  orden: z.number().int().positive(),
  tipo: z.enum(TIPOS_PREGUNTA),
  texto: z.string(),
  ayuda: z.string().nullable(),
  obligatoria: z.boolean(),
  competenciaId: z.number().int().nullable(),
  competencia: z.string().nullable(),
  escalaId: z.number().int().nullable(),
  minimo: z.number().int().nullable(),
  maximo: z.number().int().nullable(),
  /** Rótulos de los extremos. Solo una `barra` los tiene (migración 029). */
  etiquetaMin: z.string().nullable(),
  etiquetaMax: z.string().nullable(),
  /** El acento de una sección. NULL en todo lo demás. */
  color: z.string().nullable(),
  /** Decorativo, para cualquier tipo: acompaña al enunciado o al título. */
  emoji: z.string().nullable(),
  /**
   * Si esta sección tiene imagen cargada. **Solo el hecho, no el archivo**: el
   * binario se pide aparte, por su ruta autenticada. Mandar el nombre del
   * archivo tampoco serviría de nada —no se sirve como estático— y mandarlo en
   * base64 hincharía cada respuesta del constructor con megas de imagen.
   */
  tieneImagen: z.boolean(),
  /**
   * Si esta pregunta ofrece un campo abierto debajo de sus opciones (035).
   *
   * ⚠️ Lo que se escriba ahí **es texto libre**, así que en el informe sale por
   * la puerta de los textos —con su permiso, su configuración por cliente y su
   * mínimo por grupo (D-114)— y **nunca por el conteo de opciones**.
   */
  admiteObservacion: z.boolean(),
  /** Qué dice el campo. NULL usa `OBSERVACION_ROTULO_POR_OMISION`. */
  observacionRotulo: z.string().nullable(),
  /** Opciones de una pregunta de selección, o afirmaciones de una matriz. */
  items: z.array(esquemaItemPregunta),
});
export type Pregunta = z.infer<typeof esquemaPregunta>;

export const esquemaVersion = z.object({
  id: z.number().int().positive(),
  version: z.number().int().positive(),
  estado: z.enum(ESTADOS_VERSION),
  publicadaEn: z.string().nullable(),
  preguntas: z.array(esquemaPregunta),
});
export type Version = z.infer<typeof esquemaVersion>;

/**
 * Una versión **sin sus preguntas, pero con su resumen**: es lo que necesita el
 * historial para decir algo útil de cada una sin traerse el cuestionario entero.
 *
 * ⚠️ `cuantasPreguntas` **no cuenta las secciones**. Una sección es una fila de
 * `preguntas` con `tipo = 'seccion'`, así que un `count(*)` a secas diría «12
 * preguntas» de un cuestionario de 8 — y ese número aparece al lado del de la
 * lista, que sí las descuenta. Dos números distintos para lo mismo, en la misma
 * pantalla, es la clase de detalle que hace dudar de todo el informe.
 */
export const esquemaResumenVersion = z.object({
  id: z.number().int().positive(),
  version: z.number().int().positive(),
  estado: z.enum(ESTADOS_VERSION),
  publicadaEn: z.string().nullable(),
  creadoEn: z.string(),
  cuantasPreguntas: z.number().int(),
  cuantasPaginas: z.number().int(),
});
export type ResumenVersion = z.infer<typeof esquemaResumenVersion>;

export const esquemaCuestionario = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  descripcion: z.string().nullable(),
  activo: z.boolean(),
  /** Número de la versión publicada, si hay. */
  versionPublicada: z.number().int().nullable(),
  /** `true` si hay una versión en borrador esperando. */
  tieneBorrador: z.boolean(),
  preguntas: z.number().int(),
  /**
   * En cuántas páginas está repartido lo que se ve HOY. **Nunca cero**: un
   * cuestionario sin secciones sigue siendo una página, la que el trabajador
   * recorre. Así «una sola página» es exactamente «sin secciones».
   */
  paginas: z.number().int(),
  /** Si tiene portada cargada. El binario se pide aparte, como la de sección. */
  tienePortada: z.boolean(),
  creadoEn: z.string(),
});
export type Cuestionario = z.infer<typeof esquemaCuestionario>;

export const esquemaCrearCuestionario = z.object({
  nombre: z.string().trim().min(3, 'Ponle un nombre reconocible.').max(160),
  descripcion: z.string().trim().max(1000).optional(),
});
export type EntradaCrearCuestionario = z.infer<typeof esquemaCrearCuestionario>;

export const esquemaEditarCuestionario = z
  .object({
    nombre: z.string().trim().min(3).max(160).optional(),
    descripcion: z.string().trim().max(1000).nullable().optional(),
    activo: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'No enviaste ningún cambio.');
export type EntradaEditarCuestionario = z.infer<typeof esquemaEditarCuestionario>;

/**
 * Una pregunta del borrador.
 *
 * ⚠️ Se manda **la pregunta completa, con sus ítems**, y no una lista de
 * operaciones sueltas. Con «agregar opción» y «quitar opción» separados, dos
 * personas editando a la vez producen una mezcla que ninguna pidió; con el estado
 * final, la última gana — que es un resultado que alguien eligió.
 */
export const esquemaGuardarPregunta = z
  .object({
    tipo: z.enum(TIPOS_PREGUNTA),
    /** ¿Ofrece un campo abierto debajo? Solo en los tipos de `admiteObservacion`. */
    admiteObservacion: z.boolean().optional(),
    observacionRotulo: z.string().trim().max(160).nullable().optional(),
    texto: z.string().trim().min(3, 'Escribe la pregunta.').max(500),
    ayuda: z.string().trim().max(500).nullable().optional(),
    obligatoria: z.boolean().default(true),
    competenciaId: z.number().int().positive().nullable().optional(),
    escalaId: z.number().int().positive().nullable().optional(),
    minimo: z.number().int().min(0).nullable().optional(),
    maximo: z.number().int().positive().nullable().optional(),
    etiquetaMin: z.string().trim().min(1).max(60).nullable().optional(),
    etiquetaMax: z.string().trim().min(1).max(60).nullable().optional(),
    /*
      ⚠️ Un nombre de la paleta **o** un hex `#rrggbb` (036). La misma expresión
      está en `ck_preguntas_color`: acá para dar un mensaje, allá para que no
      entre nada raro. Y no es paranoia de más — un `color` sin validar viaja
      hasta el atributo `style` de la web y de la app.
    */
    color: esquemaColorSeccion.nullable().optional(),
    emoji: z.string().trim().min(1).max(16).nullable().optional(),
    /** Opciones o afirmaciones, en orden. */
    items: z.array(z.string().trim().min(1).max(300)).max(30).default([]),
  })
  .superRefine((v, ctx) => {
    /*
      Las mismas reglas que el motor, comprobadas antes de mandar. No es una
      duplicación gratuita: el CHECK de la base responde con un mensaje de
      PostgreSQL, y acá se puede decir cuál campo falta.
    */
    if (usaEscala(v.tipo) && !v.escalaId) {
      ctx.addIssue({ code: 'custom', path: ['escalaId'], message: 'Elige una escala.' });
    }
    if (!usaEscala(v.tipo) && v.escalaId) {
      ctx.addIssue({
        code: 'custom',
        path: ['escalaId'],
        message: 'Este tipo de pregunta no usa escala.',
      });
    }
    if (usaItems(v.tipo) && v.items.length < 2) {
      ctx.addIssue({
        code: 'custom',
        path: ['items'],
        message:
          v.tipo === 'matriz_likert'
            ? 'Una matriz necesita al menos dos afirmaciones.'
            : 'Una pregunta de selección necesita al menos dos opciones.',
      });
    }
    if (v.minimo != null && v.maximo != null && v.minimo > v.maximo) {
      ctx.addIssue({ code: 'custom', path: ['maximo'], message: 'El máximo no puede ser menor.' });
    }
    /*
      El tope solo se compara contra los ítems en las de selección. En una
      pregunta de `numero`, `maximo` es el valor más alto que se acepta —«¿cuántos
      años llevas?», máximo 60— y no tiene nada que ver con cuántas opciones hay.
    */
    if (v.maximo != null && usaItems(v.tipo) && v.maximo > v.items.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['maximo'],
        message: 'No se pueden pedir más opciones de las que hay.',
      });
    }
    if (!usaRango(v.tipo) && (v.minimo != null || v.maximo != null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['minimo'],
        message: 'Este tipo de pregunta no usa mínimo ni máximo.',
      });
    }
    if (v.color != null && !esSeccion(v.tipo)) {
      ctx.addIssue({
        code: 'custom',
        path: ['color'],
        message: 'El color es de las secciones.',
      });
    }
  });
export type EntradaGuardarPregunta = z.infer<typeof esquemaGuardarPregunta>;

/** Reordenar: la lista completa de ids en el orden nuevo. */
export const esquemaReordenar = z.object({
  preguntas: z.array(z.number().int().positive()).min(1).max(300),
});
export type EntradaReordenar = z.infer<typeof esquemaReordenar>;

/**
 * Límites de las imágenes de un cuestionario —la portada y la de una sección—.
 * Igual que los del logotipo: viven acá porque los tiene que conocer el navegador
 * para avisar ANTES de subir, y el servidor, que es el que de verdad decide.
 *
 * 8 MB y no 5: una portada suele venir de una cámara o de un banco de imágenes,
 * no de un archivo de marca ya optimizado.
 */
export const IMAGEN_TIPOS = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const IMAGEN_MAX_BYTES = 8 * 1024 * 1024;


/**
 * ¿La etiqueta de un punto de escala **cabe dentro de su pastilla**?
 *
 * ── El defecto que arregla ──
 * La fila de una escala dibujaba `punto.orden`, y con eso **«Del 0 al 10» salía
 * numerada 1–11**: sus etiquetas son «0»…«10» pero su `orden` va de 1 a 11, así
 * que la pastilla enseñaba la posición en vez del valor. En una escala de
 * satisfacción, un 7 que en realidad es un 6 no es un detalle de dibujo.
 *
 * La regla: si la etiqueta es corta —hasta tres caracteres, o sea un número—
 * **la etiqueta ES la pastilla**, porque es lo que el punto significa. Si es una
 * frase («Ni de acuerdo ni en desacuerdo») se muestra el ordinal y la frase se
 * revela al elegir el punto (D-131).
 *
 * Vive en `shared/` y no en el componente por dos razones: la usan la web y —el
 * día que se toque— la app, y acá se puede probar sin montar React.
 */
export const etiquetaCabeEnPastilla = (etiqueta: string): boolean => {
  const t = etiqueta.trim();
  return t.length > 0 && t.length <= 3;
};
